import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-hot-toast';

const AdminAssignQuiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter states
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [filters, setFilters] = useState({
    class_id: '',
    academic_session_id: '',
    term_id: '',
    application_status: 'approved' // Default to show only approved students
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [students, filters, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all required data in parallel
      const [studentsRes, classesRes, sessionsRes] = await Promise.all([
        adminAPI.getEnrolledStudents(), // This should return students with class, session, term info
        adminAPI.getClasses(),
        adminAPI.getAcademicSessions()
      ]);
      
      // Get the actual data arrays
      let studentsData = studentsRes.data || studentsRes;
      const classesData = classesRes.data || classesRes;
      const sessionsData = sessionsRes.data || sessionsRes;
      
      // Format students data to ensure consistent structure
      studentsData = studentsData.map(student => ({
        ...student,
        id: student.id,
        student_reg: student.student_reg,
        full_name: student.full_name || `${student.surname} ${student.othernames}`,
        surname: student.surname,
        othernames: student.othernames,
        sex: student.sex,
        application_status: student.application_status,
        class_name: student.class_name || student.class_obj?.class_name,
        class_id: student.class_id,
        academic_session_id: student.academic_session_id,
        term_id: student.term_id,
        academic_session: student.academic_session,
        term: student.term
      }));
      
      setStudents(studentsData);
      setClasses(classesData);
      setSessions(sessionsData);
      
      // Extract unique classes for filter
      const uniqueClasses = [...new Map(studentsData.map(s => [s.class_id, { id: s.class_id, name: s.class_name }])).values()].filter(c => c.id);
      setClasses(prevClasses => [...prevClasses, ...uniqueClasses]);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];
    
    // Filter by class
    if (filters.class_id) {
      filtered = filtered.filter(s => s.class_id === parseInt(filters.class_id));
    }
    
    // Filter by academic session
    if (filters.academic_session_id) {
      filtered = filtered.filter(s => s.academic_session_id === parseInt(filters.academic_session_id));
    }
    
    // Filter by term
    if (filters.term_id) {
      filtered = filtered.filter(s => s.term_id === parseInt(filters.term_id));
    }
    
    // Filter by application status
    if (filters.application_status) {
      filtered = filtered.filter(s => s.application_status === filters.application_status);
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.full_name?.toLowerCase().includes(searchLower) ||
        s.student_reg?.toLowerCase().includes(searchLower) ||
        s.surname?.toLowerCase().includes(searchLower) ||
        s.othernames?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort by surname
    filtered.sort((a, b) => (a.surname || '').localeCompare(b.surname || ''));
    
    setFilteredStudents(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const toggleStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const selectAll = () => {
    const currentPageStudents = getCurrentPageStudents();
    const allSelected = currentPageStudents.every(s => selectedStudents.includes(s.id));
    
    if (allSelected) {
      // Remove only current page students from selection
      setSelectedStudents(prev => prev.filter(id => !currentPageStudents.some(s => s.id === id)));
    } else {
      // Add all current page students to selection
      const newSelections = currentPageStudents.map(s => s.id).filter(id => !selectedStudents.includes(id));
      setSelectedStudents(prev => [...prev, ...newSelections]);
    }
  };

  const selectAllFiltered = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const getCurrentPageStudents = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredStudents.slice(startIndex, endIndex);
  };

  const handleAssign = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setSubmitting(true);
    try {
      await adminAPI.assignQuizToStudentsBulk(quizId, { 
        student_ids: selectedStudents 
      });
      toast.success(`Assigned quiz to ${selectedStudents.length} student(s)`);
      navigate(`/admin/quizzes/${quizId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign students');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'approved': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getGenderBadge = (gender) => {
    const colors = {
      'Male': 'bg-blue-100 text-blue-800',
      'Female': 'bg-pink-100 text-pink-800',
      'Other': 'bg-purple-100 text-purple-800'
    };
    return colors[gender] || 'bg-gray-100 text-gray-800';
  };

  const currentPageStudents = getCurrentPageStudents();
  const allCurrentSelected = currentPageStudents.length > 0 && 
    currentPageStudents.every(s => selectedStudents.includes(s.id));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-xl shadow border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Assign Quiz to Students</h1>
              <p className="text-gray-600 text-sm">Select students to assign this quiz</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600">Total Students</p>
              <p className="text-xl font-bold text-blue-700">{students.length}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-green-600">Filtered Students</p>
              <p className="text-xl font-bold text-green-700">{filteredStudents.length}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-xs text-purple-600">Selected</p>
              <p className="text-xl font-bold text-purple-700">{selectedStudents.length}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Classes</p>
              <p className="text-xl font-bold text-gray-700">{classes.length}</p>
            </div>
          </div>

          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
              <select
                name="class_id"
                value={filters.class_id}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Classes</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.class_name || cls.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Session</label>
              <select
                name="academic_session_id"
                value={filters.academic_session_id}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Sessions</option>
                {sessions.map(session => (
                  <option key={session.id} value={session.id}>{session.academic_session}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
              <select
                name="term_id"
                value={filters.term_id}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Terms</option>
                <option value="1">First Term</option>
                <option value="2">Second Term</option>
                <option value="3">Third Term</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="application_status"
                value={filters.application_status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="approved">Approved Only</option>
                <option value="pending">Pending Only</option>
                <option value="rejected">Rejected Only</option>
                <option value="">All Status</option>
              </select>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name or registration number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilters({
                  class_id: '',
                  academic_session_id: '',
                  term_id: '',
                  application_status: 'approved'
                });
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>

          {/* Student List */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allCurrentSelected}
                    onChange={selectAll}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({currentPageStudents.length})
                  </span>
                </div>
                <button
                  onClick={selectAllFiltered}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selectedStudents.length === filteredStudents.length ? 'Deselect All' : 'Select All Filtered'} ({filteredStudents.length})
                </button>
              </div>
              <span className="text-sm text-gray-500">
                {selectedStudents.length} of {filteredStudents.length} selected
              </span>
            </div>

            <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
              {currentPageStudents.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  <p className="font-medium">No students found</p>
                  <p className="text-sm mt-1">Try adjusting your filters or search term</p>
                </div>
              ) : (
                currentPageStudents.map(student => (
                  <div key={student.id} className="px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => toggleStudent(student.id)}
                      className="w-4 h-4 text-blue-600 rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-medium text-gray-900">
                          {student.full_name || `${student.surname} ${student.othernames}`}
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(student.application_status)}`}>
                          {student.application_status}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getGenderBadge(student.sex)}`}>
                          {student.sex || 'N/A'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Reg: {student.student_reg}</span>
                        {student.class_name && <span>Class: {student.class_name}</span>}
                        {student.academic_session && <span>Session: {student.academic_session}</span>}
                        {student.term && <span>Term: {student.term}</span>}
                        {student.dob && <span>DOB: {new Date(student.dob).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate(`/admin/quizzes/${quizId}`)}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={submitting || selectedStudents.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Assigning...
                </>
              ) : (
                `Assign to ${selectedStudents.length} Student(s)`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAssignQuiz;